import type {
  AssetRef,
  ContentfulAsset,
  ContentfulEntry,
  ContentfulIncludes,
  ContentfulSysLink,
  EntryRef,
} from "../schema/sys";

const warnedPrunedIds = new Set<string>();

// pruneIncludes removed something a component asked for, the field linking to
// it likely needs to come out of LINKED_PAGE_SKIP_FIELDS
function warnIfPruned(
  includes: ContentfulIncludes,
  type: "Entry" | "Asset",
  id: string,
) {
  if (!includes.pruned?.[type].includes(id) || warnedPrunedIds.has(id)) return;
  warnedPrunedIds.add(id);
  console.warn(
    `pruneIncludes removed ${type} ${id} but it was requested while rendering. Check LINKED_PAGE_SKIP_FIELDS in prune-includes.ts.`,
  );
}

export function resolveEntry<E extends ContentfulEntry>(
  includes: ContentfulIncludes,
  ref?: EntryRef<E>,
) {
  if (isEntryRef(ref)) {
    const entry = includes.Entry.find((entry) => entry.sys?.id === ref.sys?.id);
    if (!entry) warnIfPruned(includes, "Entry", ref.sys.id);
    return entry as E;
  } else {
    if (ref && typeof ref === "object") {
      if (
        "fields" in ref &&
        (ref as Record<string, unknown>).fields &&
        typeof (ref as Record<string, unknown>).fields === "object"
      ) {
        // is most likely an already resolved entry
        return ref as unknown as E;
      }
    }
  }
}

export function resolveAsset(
  includes: ContentfulIncludes,
  ref?: ContentfulSysLink,
): ContentfulAsset | undefined {
  if (isAssetRef(ref)) {
    const asset = includes.Asset.find((asset) => asset.sys?.id === ref.sys?.id);
    if (!asset) warnIfPruned(includes, "Asset", ref.sys.id);
    return asset!;
  } else {
    if (ref && typeof ref === "object") {
      if ("fields" in ref && ref.fields && typeof ref.fields === "object") {
        // is most likely an already resolved asset
        return ref as unknown as ContentfulAsset;
      }
    }
  }
}

export function isEntryRef(ref: unknown): ref is EntryRef<ContentfulEntry> {
  if (isLinkObj(ref)) {
    return ref.sys.linkType === "Entry";
  }

  return false;
}

export function isAssetRef(ref: unknown): ref is AssetRef {
  if (isLinkObj(ref)) {
    return ref.sys.linkType === "Asset";
  }

  return false;
}

function isLinkObj<T extends string>(
  ref: unknown,
): ref is ContentfulSysLink<string, T> {
  if (ref && typeof ref === "object") {
    if ("sys" in ref && ref.sys && typeof ref.sys === "object") {
      if ("id" in ref.sys && typeof ref.sys.id === "string") {
        if ("linkType" in ref.sys && typeof ref.sys.linkType === "string") {
          return true;
        }
      }
    }
  }

  return false;
}
